import { useSyncExternalStore } from "react";

/**
 * LA RÈGLE QU'ON REGARDE AVANT DE L'ACCEPTER.
 *
 * ⚠️ ELLE PASSE PAR LE MÊME CHEMIN QUE LE DÉFINITIF, et c'est tout l'intérêt. Une
 * seconde balise `<style>` posée par la modale aurait marché « à peu près » : à
 * spécificité égale, c'est l'ordre dans le document qui départage, donc l'aperçu
 * l'emporterait ou non selon que le panneau est rendu avant ou après le site — un
 * détail de structure qui n'a rien à voir avec le sujet, et qui changerait au premier
 * remaniement. Pire : ce qu'on regarderait ne serait pas ce qui serait enregistré.
 *
 * L'aperçu est donc FUSIONNÉ dans la feuille de l'événement, exactement comme le sera
 * la règle acceptée. Ce qu'on voit est ce qu'on obtient.
 *
 * ⚠️ HORS DU COMPOSANT, parce que celui qui l'écrit (la modale, dans le panneau) et
 * celui qui l'affiche (le site) n'ont aucun ancêtre commun utile — et parce que la
 * carte qui héberge la modale se referme au moindre écart de souris.
 */
/**
 * ⚠️ L'APERÇU DIT AUSSI CE QU'IL REMPLACE. Posé seul, il se fusionnait à sélecteur
 * égal (`mergeCss`) alors que l'acceptation retire tout ce qui visait l'élément
 * (`replaceRules`) : on aurait regardé une chose et obtenu une autre. Les deux
 * passent par la même fonction, avec la même liste.
 */
export type Preview = { css: string; replaces: readonly string[] };

let preview: Preview | null = null;
const subscribers = new Set<() => void>();

function subscribe(notify: () => void) {
  subscribers.add(notify);
  return () => {
    subscribers.delete(notify);
  };
}

/** `null` quand rien n'est en cours d'essai — la feuille enregistrée suffit alors. */
export function usePreviewCss(): Preview | null {
  return useSyncExternalStore(
    subscribe,
    () => preview,
    () => null,
  );
}

export function setPreview(
  css: string | null,
  replaces: readonly string[] = [],
): void {
  const next: Preview | null =
    css && css.trim() !== "" ? { css, replaces } : null;
  if (
    next?.css === preview?.css &&
    (next?.replaces ?? []).join("\n") === (preview?.replaces ?? []).join("\n")
  )
    return;
  preview = next;
  subscribers.forEach((notify) => notify());
}
